import { Fragment, type ReactNode } from 'react'
import {
  ANNUAL_REPORT_URL,
  FINANCIAL_REPORT_URL,
  FRIENDS_OF_DXE,
} from '~/lib/site/constants'
import { createFileRoute, Link, useHydrated } from '@tanstack/react-router'
import { pageMeta } from '~/lib/site/seo'
import { cacheHeaders } from '~/lib/site/cache'
import { Container } from '~/components/ui/Container'
import { buttonClass, type ButtonVariant } from '~/components/ui/Button'
import {
  FORM_MONTHLY,
  FORM_ONCE,
  retryFunraise,
  useFunraise,
  type FunraiseStatus,
} from '~/lib/funraise'
import { Img } from '~/components/ui/Img'
import chickenRescue from '~/assets/images/donate-chicken-rescue.jpg?responsive'
import { FaqAccordion } from '~/components/sections/FaqAccordion'
import type { FaqItem } from '~/components/sections/FaqAccordion'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'

export const Route = createFileRoute('/donate')({
  staticData: { minimalHeader: true },
  // `?donated=true` is where the old PayPal flow returned to. The page is
  // prerendered without it, so it only applies after hydration.
  validateSearch: (search: Record<string, unknown>): { donated?: true } => ({
    donated:
      search.donated === true || search.donated === 'true' ? true : undefined,
  }),
  headers: () => cacheHeaders.content,
  head: () =>
    pageMeta({
      title: 'Donate',
      description:
        'Help us change the world for animals. Donate to Friends of DxE, a supporting non-profit of Direct Action Everywhere.',
      ogDescription: 'Help us change the world for animals.',
    }),
  component: Donate,
})

const mail = (
  <a
    href={`mailto:${FRIENDS_OF_DXE.email}`}
    className="font-bold hover:underline"
  >
    {FRIENDS_OF_DXE.email}
  </a>
)
const address = (
  <>
    {FRIENDS_OF_DXE.legalName}
    {FRIENDS_OF_DXE.addressLines.map((line) => (
      <Fragment key={line}>
        <br />
        {line}
      </Fragment>
    ))}
  </>
)

const faqs: FaqItem[] = [
  {
    question: 'What is the preferred way to give?',
    answer: (
      <>
        <p className="mb-4">
          All donations are greatly appreciated, and monthly giving is an easy
          and high-impact way to help us with our long-term mission. You can
          change or cancel your monthly donation at any time.
        </p>
        <p className="mb-4">
          Being a monthly donor or becoming an Open Rescue Ally (by donating
          $1000+) comes with extra benefits, including an exclusive monthly call
          with DxE leadership.
        </p>
        <p>
          Donating by check or wire transfer is the best way to donate if
          you&apos;re looking to make a larger donation and minimize transaction
          fees. Please email donate@friendsofdxe.com for wire transfer
          information. Checks payable to Friends of DxE can be mailed to the
          following address:
          <br />
          <br />
          {address}
        </p>
      </>
    ),
  },
  {
    question: 'Do you accept check or employer match?',
    answer: (
      <>
        <p className="mb-4">
          Yes! You can donate by sending a check, payable to FRIENDS OF DXE, to
          the following address:
          <br />
          <br />
          {address}
        </p>
        <p className="mb-4">
          Please notify {mail} if a check is in the mail so we can look out for
          it.
        </p>
        <p>
          If your employer offers a matching donation program, please complete a
          one-time or monthly donation above, and be sure to check the
          &quot;Company Match&quot; option when prompted. You will then receive
          an email with more information to complete the match.
        </p>
      </>
    ),
  },
  {
    question: 'Do you accept stock, Venmo, or cryptocurrency?',
    answer: (
      <p>
        Yes to all! To donate stock, just notify {mail} and we’ll send you
        instructions. You can also send a gift to Direct Action Everywhere (a
        separate non-profit) through{' '}
        <a
          href="https://account.venmo.com/u/directactioneverywhere"
          className="font-bold hover:underline"
        >
          Venmo
        </a>{' '}
        or{' '}
        <a
          href="https://commerce.coinbase.com/pay/d7311ca8-4b2b-4c30-88fb-0bf948ee8959"
          target="_blank"
          rel="noreferrer"
          className="font-bold hover:underline"
        >
          cryptocurrency
        </a>
        .
      </p>
    ),
  },
  {
    question: 'How can I make a legacy gift?',
    answer: (
      <>
        <p className="mb-4">
          Designating Friends of DxE as a beneficiary helps us continue our work
          well into the future. Please let us know at {mail} if you are
          considering adding Friends of DxE as a beneficiary or have any
          questions about the process.
        </p>
        <p>
          You can use the following information:
          <br />
          Legal name: {FRIENDS_OF_DXE.legalName}
          <br />
          EIN: {FRIENDS_OF_DXE.ein}
          <br />
          Address: {FRIENDS_OF_DXE.addressLines.join(', ')}
        </p>
      </>
    ),
  },
  {
    question: 'Where does my money go?',
    answer: (
      <>
        <p className="mb-4">
          Your gift goes towards achieving revolutionary social and political
          change for animals, exposing and ending the animal agriculture
          industry and bringing animal voices into the courtroom.
        </p>
        <p className="mb-4">
          <a
            href={ANNUAL_REPORT_URL}
            target="_blank"
            rel="noreferrer"
            className="font-bold hover:underline"
          >
            Click here to see our 2025 Impact Report
          </a>
        </p>
        <p className="mb-4">
          {FRIENDS_OF_DXE.legalName} (EIN {FRIENDS_OF_DXE.ein}), a tax-exempt
          501(c)(3) organization, is a California nonprofit public benefit
          corporation that provides grants for DxE&apos;s campaigns. The board
          and leadership of Friends of DxE is independent of each of the
          grassroots chapters that comprise DxE&apos;s worldwide network.
        </p>
        <p className="mb-4">
          <a
            href={FINANCIAL_REPORT_URL}
            target="_blank"
            rel="noreferrer"
            className="font-bold hover:underline"
          >
            Download the Financial Transparency report
          </a>
        </p>
        <p>To see past Form 990s for Friends of DxE, please email {mail}.</p>
      </>
    ),
  },
  {
    question:
      'Is my donation tax-deductible? What is the tax ID number of Friends of DxE?',
    answer: (
      <>
        <p className="mb-4">
          Yes! All donations in the United States are tax-deductible to the
          fullest extent of the law. No goods or services are provided in
          exchange for your donation.
        </p>
        <p>
          Friends of DxE&apos;s Federal Tax Identification Number is{' '}
          {FRIENDS_OF_DXE.ein}.
        </p>
      </>
    ),
  },
  {
    question: 'What are other ways to support DxE?',
    answer: (
      <p>
        You can create a fundraiser for Direct Action Everywhere (a separate
        non-profit) on{' '}
        <a
          href="https://www.facebook.com/fund/directactioneverywhere/"
          className="font-bold hover:underline"
        >
          Facebook by clicking here
        </a>
        .
        <br />
        <br />
        You can also find a{' '}
        <Link to="/events" className="font-bold hover:underline">
          DxE event near you
        </Link>{' '}
        and meet like-minded people and help us change the world for animals.
      </p>
    ),
  },
  {
    question:
      'How do I provide matching donations to help DxE double its impact?',
    answer: (
      <p>
        Our fundraising team regularly coordinates campaigns, and we&apos;re
        always looking for donors to offer a match! If you&apos;re interested in
        offering a match to help us double our impact, please contact {mail}.
      </p>
    ),
  },
  {
    question: 'What if I have more questions about donations?',
    answer: (
      <p>Please contact {mail} and we will reply to you within 48 hours.</p>
    ),
  },
  {
    question: 'How do I change or cancel my monthly donation?',
    answer: (
      <p>
        To change or cancel your monthly donation, please fill out{' '}
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLSf-Y7Lb14G3jsVF9tamfWdU23hWqWgcoVftOu3BqtePxagLrA/viewform"
          target="_blank"
          rel="noreferrer"
        >
          this form
        </a>
        .
      </p>
    ),
  },
]

function DonateButton({
  formId,
  status,
  variant,
  children,
}: {
  formId: number
  status: FunraiseStatus
  variant: ButtonVariant
  children: ReactNode
}) {
  const loading = status === 'loading'
  // Funraise opens its modal from a click on [data-formid] once the form's
  // iframe exists; until then the button is disabled. After a failure, the
  // click retries the load.
  return (
    <button
      type="button"
      data-formid={formId}
      disabled={loading}
      aria-busy={loading}
      onClick={() => {
        if (status === 'failed') retryFunraise()
      }}
      // Loading: the label stays and pulses, with no spinner, so the button
      // doesn't change width when Funraise is ready.
      className={buttonClass(
        variant,
        loading ? 'animate-pulse opacity-70' : 'cursor-pointer',
      )}
    >
      {children}
    </button>
  )
}

function Donate() {
  const hydrated = useHydrated()
  const search = Route.useSearch()
  const donated = hydrated && search.donated === true
  const funraise = useFunraise()

  return (
    <>
      {/* Focused header: the Webflow donate page shows only the logo. */}
      {/* 16:9 at desktop, but grows when the panel's text needs more room
          (narrow desktop widths) instead of clipping it. */}
      <section className="bg-black lg:relative lg:flex lg:min-h-[56.25vw] lg:items-center lg:overflow-hidden lg:py-10">
        <div className="relative h-[250px] overflow-hidden md:h-[300px] lg:absolute lg:inset-y-0 lg:left-0 lg:h-auto lg:w-[66%]">
          <Img
            image={chickenRescue}
            sizes="(min-width: 992px) 66vw, 100vw"
            priority
            alt="A rescued chicken"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        {/* Panel floats over the photo with a gap on the right, like Webflow. */}
        <div className="bg-surface lg:relative lg:ml-auto lg:w-[42%] lg:bg-transparent xl:w-[34%]">
          <div className="bg-surface/90 p-[30px] lg:mr-10 xl:mr-[60px]">
            <div className="mb-2.5 text-sm font-medium tracking-wide text-fg-muted uppercase">
              Donate
            </div>
            <h1 className="mt-0 mb-4 text-[28px] leading-9 md:text-[34px] md:leading-[42px]">
              <strong>
                {donated
                  ? 'Thank you for your donation!'
                  : 'Help us change the world for animals.'}
              </strong>
            </h1>
            <p className="mb-6 text-lg leading-[26px]">
              {donated ? (
                'You will receive a receipt via email.'
              ) : (
                <>
                  DxE is going up against the most destructive industries on the
                  planet.{' '}
                  <a
                    href={ANNUAL_REPORT_URL}
                    className="font-bold text-accent hover:underline"
                  >
                    We are exposing abuse, rescuing animals, changing the law,
                    and transforming society.
                  </a>{' '}
                  Help us keep the momentum going by donating to a supporting
                  non-profit today.
                </>
              )}
            </p>
            {!donated && (
              <>
                <div className="flex flex-wrap gap-3 max-md:flex-col">
                  <DonateButton
                    formId={FORM_MONTHLY}
                    status={funraise}
                    variant="solid"
                  >
                    Monthly donation
                  </DonateButton>
                  <DonateButton
                    formId={FORM_ONCE}
                    status={funraise}
                    variant="outline"
                  >
                    One-time donation
                  </DonateButton>
                </div>
                {funraise === 'failed' && (
                  <p className="pt-3 text-sm text-fg-muted">
                    The donation form didn’t load. Tap a button to try again, or
                    email {mail} and we’ll help you give.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="pt-10 lg:pt-[55px]">
        <Container className="rounded-lg bg-surface-alt px-5 py-10 md:py-[50px]">
          <div className="mx-auto max-w-[680px]">
            <div className="text-sm font-medium tracking-wide text-fg-muted uppercase">
              FAQ about donations
            </div>
            <FaqAccordion items={faqs} />
          </div>
        </Container>
      </section>

      <div className="pt-14">
        <DonateSignupBand
          showDonate={false}
          stripe={false}
          signupTitle="Join our network"
        />
      </div>
    </>
  )
}
